import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildForm,
  buildLink,
  buildUrl,
  ContentRoute,
  RouteSlotInput,
} from '@caritas/ui';
import { useRef } from 'react';
import { createStore, useStore } from 'zustand';

import { useLocationFilter } from '../../commons/location';
import { useQueryParams } from '../../commons/query-string';
import { useSiteSettings } from '../../commons/site-settings';
import { useTaxonomies } from '../../commons/taxonomies';
import { languageFallback } from '../../utils/localizations';

type LocationSearchItemType = {
  id: string;
  title: string;
  path: string;
  bioLocation?: boolean;
  sosLocation?: boolean;
  workTypes : Array<{
    drupalId: string;
  }>
  languages: Array<{
    drupalId: string
  }>
  canton: {
    drupalId: string
  }
}

type FormFiltersType = {
  startdate?: any;
  enddate?: any;
  remainingSpots?: any;
  language?: any;
  type?: any;
  canton?: any;
  bio?: any;
  sos?: any;
}

type FacetedFilterOptionType = {
  id: string;
  label: string;
  depth: number;
  originalLabel?: string;
}

type FacetedFiltersType = {
  languages: Array<FacetedFilterOptionType>;
  types: Array<FacetedFilterOptionType>;
  cantons: Array<FacetedFilterOptionType>;
  hasBioLocations: boolean;
  hasSOSLocations: boolean;
};

type ViewTypeState = {
  viewType: string;
  update: (viewType: string) => void;
};

type FilteredResultsAmountState = {
  amount?: number;
  update: (amount: number) => void;
}

type AvailableFiltersState = {
  filters?: FacetedFiltersType;
  update: (filters: FacetedFiltersType) => void;
}

const viewTypeStore = createStore<ViewTypeState>((set) => ({
  viewType: 'map',
  update: (viewType: string) => {
    set({
      viewType: viewType,
    });
  },
}));

const filteredResultsAmountStore = createStore<FilteredResultsAmountState>(
  (set) => ({
    amount: undefined,
    update: (amount: number) => {
      set({
        amount: amount,
      })
    }
  }),
);

const availableFiltersStore = createStore<AvailableFiltersState>(
  (set) => ({
    filters: undefined,
    update: (filters: FacetedFiltersType) => {
      set({
        filters: filters
      })
    }
  })
);

const itemMatchesFilters = (item: LocationSearchItemType, selectedFilters: FormFiltersType) => {
  const languageIntersectionExists = selectedFilters.language &&
    selectedFilters.language.length > 0 ? item.languages.filter(x => selectedFilters.language.includes(x.drupalId)).length > 0 : true;
  if (!languageIntersectionExists) {
    return false;
  }

  const workTypeIntersectionExists = selectedFilters.type &&
    selectedFilters.type.length > 0 ? item.workTypes.filter(x => selectedFilters.type.includes(x.drupalId)).length > 0 : true;
  if (!workTypeIntersectionExists) {
    return false;
  }

  const cantonIntersectionExists = selectedFilters.canton &&
    selectedFilters.canton.length > 0 ? selectedFilters.canton.includes(item.canton.drupalId) : true;
  if (!cantonIntersectionExists) {
    return false;
  }

  const bioExists = selectedFilters.bio ? item.bioLocation : true;
  if (!bioExists) {
    return false;
  }

  const sosExists = selectedFilters.sos ? item.sosLocation : true;
  if (!sosExists) {
    return false;
  }

  return true;
}

export function mapLocationWidget(
  viewMode: string,
  pageContext: SilverbackPageContext,
): RouteSlotInput<typeof ContentRoute, 'body', 'filteredLocations'> {
  return {
    key: 'filteredLocations',
    input: function useLocationWidget() {
      const queryParams = useQueryParams();
      if (typeof queryParams.viewType === 'string') {
        viewTypeStore.getState().update(queryParams.viewType);
      }
      const taxonomies = useTaxonomies(pageContext);

      const buildOptions = (
        items: Array<{ drupalId: string; name: string; originalName?: string }>,
        availableFilters?: Set<string>,
        currentFilters?: Set<string>,
      ) => {
        const options: Array<FacetedFilterOptionType> = [];
        items.map((item) => {
          // When deciding if a filter opiton should be returned, we need to
          // check if either it is among the available filters (which is built
          // based on the current result set after applying the selected
          // filters) or if it is among the currently selected ones, even if it
          // should not be avaible, otherwise the user will not be able to
          // deselect it.
          if (
            !availableFilters ||
            availableFilters.has(item.drupalId) ||
            (currentFilters && currentFilters.has(item.drupalId))
          ) {
            options.push({
              id: item.drupalId,
              label: item.name,
              depth: 0,
              originalLabel: item.originalName,
            });
          }
        });
        return options;
      };

      const {
        markers,
        gridItems,
        setFilters,
        allLocations,
        currentValues,
        hasMoreItems,
        pager,
        isLoadingData,
      } = useLocationFilter(
        languageFallback(pageContext.locale || 'de'),
        viewMode
      );
      const setFilteredResultsAmount = useStore(
        filteredResultsAmountStore,
        (state) => state.update,
      );

      const updateAvailableFilters = useStore(
        availableFiltersStore,
        (state) => state.update,
      );
      const selectedFilters = useRef(currentValues);

      const { locationSearchPage } = useSiteSettings(pageContext);
      return [
        {
          Form: buildForm({
            initialValues: {
              startdate: selectedFilters.current.startdate || '',
              enddate: selectedFilters.current.enddate || '',
              type: selectedFilters.current.type || '',
              language: selectedFilters.current.language || '',
              canton: selectedFilters.current.canton || '',
              remainingSpots: selectedFilters.current.remainingSpots || '',
              bio: selectedFilters.current.bio || false,
              sos: selectedFilters.current.sos || false,
            },
            onChange: (values) => {
              // When the form changes (the user selects/deselects different
              // filters), we want to do two things:
              // 1. Update the filtered results amount, which is the numnber
              // shown on the Submit button, so that user knows how many results
              // would be returned for their selected filters.
              // 2. Update tha available filters, to obtain a faceted behaviour
              // so that the user can only select options that will returns
              // results.

              // Step 1: update the filtered results amount.
              selectedFilters.current = values;
              const matches = allLocations.reduce((accumulator, currentValue) => {
                if (itemMatchesFilters(currentValue, values)) {
                  return accumulator + 1;
                }
                return accumulator;
              }, 0);
              setFilteredResultsAmount(matches);

              // Step 2: update available filters.
              const availableFilters: {
                languages: Set<string>,
                types: Set<string>,
                cantons: Set<string>,
                hasBioLocations: boolean,
                hasSOSLocations: boolean,
              } = {
                languages: new Set(),
                types: new Set(),
                cantons: new Set(),
                hasBioLocations: false,
                hasSOSLocations: false,
              };
              allLocations.map(item => {
                // If the current item does not match the selected filters, then
                // we do not want to add its categories (language, work type,
                // etc.) to the available filters.
                // In order to get the available items for a particular filter,
                // we actually need to check if an item matches filters,
                // excluding that particular filter. For example, if we want to
                // get all the available language options, we check if the item
                // matches the selected filters excluding the currently selected
                // language, otherwise we actually loose results.
                if (itemMatchesFilters(item, {...values, language: []})) {
                  item.languages.map(language => {
                    availableFilters.languages.add(language.drupalId);
                  });
                }
                if (itemMatchesFilters(item, {...values, type: []})) {
                  item.workTypes.map(worktype => {
                    availableFilters.types.add(worktype.drupalId);
                  });
                }
                if (itemMatchesFilters(item, {...values, canton: []})) {
                  availableFilters.cantons.add(item.canton.drupalId);
                }
                if (item.bioLocation && itemMatchesFilters(item, {...values, bio: false})) {
                  availableFilters.hasBioLocations = true;
                }
                if (item.sosLocation && itemMatchesFilters(item, {...values, sos: false})) {
                  availableFilters.hasSOSLocations = true;
                }
              });
              updateAvailableFilters({
                types: buildOptions(
                  taxonomies.workTypes,
                  availableFilters.types,
                  new Set(selectedFilters.current.type),
                ),
                languages: buildOptions(
                  taxonomies.languages,
                  availableFilters.languages,
                  new Set(selectedFilters.current.language),
                ),
                cantons: buildOptions(
                  taxonomies.cantons,
                  availableFilters.cantons,
                  new Set(selectedFilters.current.canton),
                ),
                hasBioLocations: availableFilters.hasBioLocations,
                hasSOSLocations: availableFilters.hasSOSLocations,
              });
            },
            onSubmit: (values) => {
              // On submit, we actually want to do a re-render and also update
              // the browser url, so we do a state update here.
              setFilters(JSON.stringify(values));
            },
          }),
          showAllFilters:
            currentValues.canton ||
            currentValues.remainingSpots ||
            currentValues.bio ||
            currentValues.sos,
          markers: markers,
          gridItems: gridItems,
          filteredResultsAmountStore,
          availableFiltersStore,
          viewTypeStore: viewTypeStore,
          isLoadingData,
          MoreLink: hasMoreItems
            ? buildLink({
                href: buildUrl(
                  [locationSearchPage?.path],
                  {
                    ...queryParams,
                    viewType: 'list',
                  },
                  undefined,
                  'search',
                ),
              })
            : undefined,
          pagerLinks: pager && {
            FirstPage: pager.firstPage,
            PreviousPage: pager.previousPage,
            NextPage: pager.nextPage,
            LastPage: pager.lastPage,
            pages: pager.pages,
            hasMissingLeftPages: pager.hasMissingLeftPages,
            hasMissingRightPages: pager.hasMissingRightPages,
          },
        },
        200,
      ];
    },
  };
}

export const mapLocationSearchBlock = (pageContext: SilverbackPageContext) =>
  function (
    input: BlockLocationSearchFragment,
  ): RouteSlotInput<typeof ContentRoute, 'body', 'filteredLocations'> {
    return mapLocationWidget(input.viewMode, pageContext);
  };
